import Decimal from "decimal.js-light";
/**
 * Calculates the Greatest Common Divisor (GCD) of two numbers.
 * Used for simplifying fractions.
 *
 * @param {number} a - First number
 * @param {number} b - Second number
 * @returns {number} The GCD of a and b
 */
const gcd = (a, b) => {
  if (b === 0) {
    return a;
  }
  return gcd(b, a % b);
};

/**
 * Converts a decimal number to a simplified fraction string.
 * Prefers common denominators (2, 4, 8, 16, 32, 64) for readability.
 *
 * @param {number} decimal - The decimal value to convert (e.g., 0.5, 0.25, 0.75)
 * @param {number} [tolerance=0.0001] - Tolerance for floating point comparison
 * @returns {string} The fraction string (e.g., "1/2", "1/4", "3/4") or empty string if no fraction
 */
export const decimalToFraction = (decimal, tolerance = 0.0001) => {
  if (Math.abs(decimal) < tolerance) {
    return "";
  }

  // Try common denominators first (most readable for US measurements)
  const commonDenominators = [2, 4, 8, 16, 32, 64];
  for (const denom of commonDenominators) {
    const numerator = Math.round(decimal * denom);
    const fraction = numerator / denom;
    if (Math.abs(decimal - fraction) < tolerance) {
      // Simplify the fraction
      const divisor = gcd(Math.abs(numerator), denom);
      const simplifiedNum = numerator / divisor;
      const simplifiedDen = denom / divisor;
      return `${simplifiedNum}/${simplifiedDen}`;
    }
  }

  // If no common denominator works, try to find a simplified fraction
  // Limit denominator to 64 for readability
  for (let denom = 2; denom <= 64; denom++) {
    const numerator = Math.round(decimal * denom);
    const fraction = numerator / denom;
    if (Math.abs(decimal - fraction) < tolerance) {
      const divisor = gcd(Math.abs(numerator), denom);
      const simplifiedNum = numerator / divisor;
      const simplifiedDen = denom / divisor;
      return `${simplifiedNum}/${simplifiedDen}`;
    }
  }

  // If we can't find a good fraction, return empty string
  // (will fall back to decimal display)
  return "";
};

/**
 * Formats decimal inches as a fraction string.
 * Converts decimal inches to fraction format (e.g., 5.5 → "5 1/2").
 *
 * @param {number} numInches - The number of inches as a decimal
 * @returns {string} The inches as a fraction string (e.g., "5 1/2", "1/2", "5", "0")
 */
export const formatInchesAsFraction = (numInches) => {
  if (numInches === 0) {
    return "0";
  }

  // Round to nearest 1/32 so we only show standard measurement fractions (1/4, 1/2, 3/4, etc.),
  // not odd ones like 6/25 that come from float/rounding (e.g. 3 ft 1 1/4 in stored as 945.9 mm → 1.24 in)
  const roundedToThirtySecond = Math.round(numInches * 32) / 32;
  const wholeInches = Math.floor(roundedToThirtySecond);
  const fractionalPart = roundedToThirtySecond - wholeInches;

  // Convert fractional part to fraction (only 2,4,8,16 will match now)
  const fractionStr = decimalToFraction(fractionalPart);

  if (fractionStr) {
    // We have a fractional part
    if (wholeInches === 0) {
      // Only fraction, no whole inches
      return fractionStr;
    }
    // Both whole inches and fraction
    return `${wholeInches}-${fractionStr}`;
  }

  // No valid fraction found, try rounding to nearest 1/32
  const roundedInches = Math.round(numInches * 32) / 32;
  if (roundedInches === Math.floor(roundedInches)) {
    return `${Math.floor(roundedInches)}`;
  }

  // Try to convert the rounded value
  const roundedFraction = decimalToFraction(roundedInches - Math.floor(roundedInches));
  if (roundedFraction) {
    const whole = Math.floor(roundedInches);
    if (whole === 0) {
      return roundedFraction;
    }
    return `${whole}-${roundedFraction}`;
  }

  // Fallback to decimal with limited precision
  return Decimal(numInches).toDecimalPlaces(2);
};

/**
 * Formats feet and inches with fractional inches displayed as fractions.
 * Converts decimal inches to fraction format (e.g., 5.5 → "5 1/2").
 *
 * @param {Object} [params={}] - The measurement object
 * @param {number} [params.feet=0] - The number of feet
 * @param {number} [params.inches=0] - The number of inches (can be fractional)
 * @returns {{feet: number, inches: string}} An object with feet (number) and inches (string with fraction format)
 */
export const formatFeetInchesWithFraction = ({ feet = 0, inches = 0 } = {}) => {
  const numFeet = Number(feet) || 0;
  const numInches = Number(inches) || 0;

  return {
    feet: numFeet,
    inches: formatInchesAsFraction(numInches),
  };
};

/**
 * Parses fractional inches from various formats.
 * Supports formats like "5 1/2", "5.5", "1/2", "5", etc.
 *
 * @param {number|string} inches - The inches value (can be a number, decimal string, or fraction string)
 * @returns {number} The decimal inches value
 */
export const parseFractionalInches = (inches) => {
  if (typeof inches === "number") {
    return inches;
  }

  if (typeof inches !== "string") {
    return Number(parseFloat(inches)) || 0;
  }

  const trimmed = inches.trim();

  // If it's already a decimal number string, parse it directly
  if (/^-?\d*\.?\d+$/.test(trimmed)) {
    return Number(parseFloat(trimmed)) || 0;
  }

  // Try to parse "whole separator fraction" format like "5-1/2", "5 1/2"
  // Separator (space or hyphen) is REQUIRED to distinguish from pure fractions like "44/8"
  const wholeFractionPattern = /^(-?\d+)[\s-]+(\d+)\/(\d+)$/;
  const wholeMatch = trimmed.match(wholeFractionPattern);

  if (wholeMatch) {
    const wholePart = Number(parseFloat(wholeMatch[1]));
    const numerator = Number(parseFloat(wholeMatch[2]));
    const denominator = Number(parseFloat(wholeMatch[3]));

    if (denominator === 0) {
      return wholePart;
    }

    return wholePart + numerator / denominator;
  }

  // Try to parse pure fraction like "1/2", "44/8" (no whole part, no separator)
  const pureFractionPattern = /^(\d+)\/(\d+)$/;
  const pureMatch = trimmed.match(pureFractionPattern);

  if (pureMatch) {
    const numerator = Number(parseFloat(pureMatch[1]));
    const denominator = Number(parseFloat(pureMatch[2]));

    if (denominator === 0) {
      return 0;
    }

    return numerator / denominator;
  }

  // If no pattern matches, try to parse as a number
  return Number(parseFloat(trimmed)) || 0;
};

/**
 * Validates inches input. Accepts numbers and fractions like "1 3/4".
 * Rejects empty, whitespace-only, or values containing letters.
 *
 * @param {string|number} value - The inches value to validate
 * @returns {boolean} True if valid (number or fraction format)
 */
export const isValidInchesInput = (value) => {
  if (value === undefined || value === null) {
    return false;
  }
  const trimmed = String(value).trim();
  if (trimmed === "") {
    return false;
  }
  if (/[a-zA-Z]/.test(trimmed)) {
    return false;
  }
  if (/^-?\d*\.?\d+$/.test(trimmed)) {
    return true;
  }
  const fractionMatch = trimmed.match(/^(-?\d+)?[\s-]*(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const denominator = Number(fractionMatch[3]);
    return denominator !== 0; // Denominator 0 should be invalid.
  }

  return false;
};
